import { useEffect, useState } from 'react';
import { Modal, List, Icon, Button, Form } from 'semantic-ui-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'common/toast';
import FileViewer from './FileViewer';
import { useGetFileUploadQuery, useVoteFileUploadMutation } from '../api/repositoryApi';
import { FileTypes, getFileTypeIcon } from 'common/constants/filetypes';
import { openResourceLink } from 'common/utils/resourceLinks';
import KnowledgeBaseContentShareModal from 'areas/knowledge-base/components/KnowledgeBaseContentShareModal';
import { BodyText, H3 } from 'common/styles/page';
import styled from 'styled-components';
import { PositionedModal } from 'common/components/PositionedModal';
import { getMemberId, isCurrentUserStaffOrSuperAdmin, isCurrentMemberOrgAdmin } from 'common/selectors/auth';

type Resource = {
  id: number;
  name: string;
  type: number;
  object_key: string;
};

type ResourceListModalProps = {
  resources: Resource[];
  isOpen: boolean;
  setModalOpen: (open: boolean) => void;
};

type VoteType = 1 | -1;

export const ResourceListModal = ({ resources, isOpen, setModalOpen }: ResourceListModalProps) => {
  const [selectedFileResource, setSelectedFileResource] = useState<Resource | null>(
    resources[0] || null
  );
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [isFeedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [isVotePending, setIsVotePending] = useState(false);
  const [voteFileUpload, { isLoading: isVoteLoading }] = useVoteFileUploadMutation();
  const { addToast } = useToasts();
  const currentEmployeeId = useSelector(getMemberId);
  
  const { data: fileData, isFetching: isFileFetching, isLoading: isFileLoading } =
    useGetFileUploadQuery(selectedFileResource?.id.toString() ?? '', {
      skip: !isOpen || !selectedFileResource,
    });
  const isFileLoadedForCurrentFile = Boolean(
    fileData && String(fileData.id) === String(selectedFileResource?.id)
  );

  const isFullAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isOrgAdmin = useSelector(isCurrentMemberOrgAdmin);
  const canShare = isFullAdmin || isOrgAdmin;

  const canVote = !isVotePending && !isFileFetching && !isFileLoading && isFileLoadedForCurrentFile;

  const handleVoteButtonClick = async (vote: VoteType) => {
    if (!canVote || !selectedFileResource) {
      return;
    }

    if (vote === 1) {
      setIsVotePending(true);
      try {
        await voteFileUpload({
          id: selectedFileResource.id,
          vote,
          employee_id: currentEmployeeId || undefined,
        }).unwrap();
        addToast('Thanks for your feedback!', { appearance: 'success' });
      } catch (err) {
        // swallow errors; API handles validation.
      } finally {
        setIsVotePending(false);
      }
      return;
    }

    setFeedbackNotes(fileData?.your_feedback_notes ?? '');
    setFeedbackModalOpen(true);
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackNotes('');
    setFeedbackModalOpen(false);
  };

  const handleSubmitFeedback = async () => {
    if (!selectedFileResource) {
      handleCloseFeedbackModal();
      return;
    }

    setIsVotePending(true);
    try {
      await voteFileUpload({
        id: selectedFileResource.id,
        vote: -1,
        feedback_notes: feedbackNotes || undefined,
        employee_id: currentEmployeeId || undefined,
      }).unwrap();
      addToast('Thanks for your feedback!', { appearance: 'success' });
    } catch (err) {
      // swallow errors; API handles validation.
    } finally {
      setIsVotePending(false);
      handleCloseFeedbackModal();
    }
  };

  useEffect(() => {
    if (isOpen) {
      const firstPdf = resources.find(r => r.type === FileTypes.PDF);
      const firstMp4 = resources.find(r => r.type === FileTypes.MP4);

      const firstViewable = firstPdf || firstMp4;
      setSelectedFileResource(firstViewable || null);
    }
  }, [isOpen, resources]);

  return (
    <>
      <PositionedModal
        open={isOpen}
        onClose={() => setModalOpen(false)}
        size="large"
        closeIcon
        dimmer={{ style: { zIndex: 11000 } }}
        style={{ width: '90vw', maxWidth: '1400px', maxHeight: 'calc(100vh - 64px)' }}
      >
      <Modal.Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{selectedFileResource?.name || 'nuLogic Resources'}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {selectedFileResource && (
              <>
                <Button
                  icon
                  title="Helpful"
                  aria-label="Helpful"
                  color={!canVote ? 'grey' : fileData?.your_vote === 1 ? 'green' : 'grey'}
                  size="small"
                  onClick={() => handleVoteButtonClick(1)}
                  disabled={!canVote || isVoteLoading}
                >
                  <Icon name="thumbs up" />
                </Button>
                <Button
                  icon
                  title="Not helpful"
                  aria-label="Not helpful"
                  color={!canVote ? 'grey' : fileData?.your_vote === -1 ? 'red' : 'grey'}
                  size="small"
                  onClick={() => handleVoteButtonClick(-1)}
                  disabled={!canVote || isVoteLoading}
                >
                  <Icon name="thumbs down" />
                </Button>
              </>
            )}
            {selectedFileResource && canShare && (
              <Button
                icon
                labelPosition="left"
                color="blue"
                size="small"
                onClick={() => setShareModalOpen(true)}
              >
                <Icon name="share" />
                Share resource
              </Button>
            )}
          </div>
        </div>
      </Modal.Header>
      <Modal.Content style={{ height: 'calc(100vh - 180px)', overflow: 'hidden', minHeight: 0 }}>
        <div style={{ display: 'flex', height: '100%' }}>
          {/* Left Column: File List */}
          <div
            style={{
              width: '30%',
              minWidth: '300px',
              maxWidth: '400px',
              borderRight: '1px solid #ddd',
              paddingRight: '1em',
              overflowY: 'auto',
            }}
          >
            <List selection>
              {/* PDFs Section */}
              {resources.some(r => r.type === FileTypes.PDF) && (
                <div style={{ marginBottom: '1.5em' }}>
                  <List.Header>
                    <H3>PDFs</H3>
                  </List.Header>
                  {resources
                    .filter(resource => resource.type === FileTypes.PDF)
                    .map(resource => (
                      <List.Item
                        key={resource.id}
                        active={selectedFileResource?.id === resource.id}
                        onClick={() => setSelectedFileResource(resource)}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ flexGrow: 1 }}>
                            {getFileTypeIcon(resource.type)} {resource.name}
                          </div>
                        </div>
                      </List.Item>
                    ))}
                </div>
              )}

              {/* MP4s Section */}
              {resources.some(r => r.type === FileTypes.MP4) && (
                <div style={{ marginBottom: '1.5em' }}>
                  <List.Header>
                    <H3>Videos</H3>
                  </List.Header>
                  {resources
                    .filter(resource => resource.type === FileTypes.MP4)
                    .map(resource => (
                      <List.Item
                        key={resource.id}
                        active={selectedFileResource?.id === resource.id}
                        onClick={() => setSelectedFileResource(resource)}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ flexGrow: 1 }}>
                            <BodyText>
                              {getFileTypeIcon(resource.type)} {resource.name}
                            </BodyText>
                          </div>
                        </div>
                      </List.Item>
                    ))}
                </div>
              )}

              {/* URLs Section */}
              {resources.some(r => r.type === FileTypes.URL) && (
                <div style={{ marginBottom: '1.5em' }}>
                  <List.Header>
                    <H3>Links</H3>
                  </List.Header>
                  {resources
                    .filter(resource => resource.type === FileTypes.URL)
                    .map(resource => (
                      <List.Item
                        key={resource.id}
                        onClick={() =>
                          openResourceLink(resource, {
                            openInApp: () => setSelectedFileResource(resource),
                            openInNewTab: url => window.open(url, '_blank', 'noopener'),
                          })
                        }
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ flexGrow: 1 }}>
                            {getFileTypeIcon(resource.type)} {resource.name}
                          </div>
                        </div>
                      </List.Item>
                    ))}
                </div>
              )}
            </List>
          </div>

          {/* Right Column: File Viewer */}
          <div
            style={{
              flex: 1,
              paddingLeft: '1em',
              width: '100%',
              maxWidth: '100%',
              overflowY: 'auto',
            }}
          >
            {selectedFileResource ? (
              <FileViewer isFullScreen={false} fileId={selectedFileResource.id.toString()} />
            ) : (
              <div style={{ textAlign: 'center', marginTop: '3rem' }}>
                <Icon name="file outline" size="huge" color="grey" />
                <div style={{ fontSize: '1.2em', marginTop: '1rem', color: '#888' }}>
                  Select a resource from the left to view content
                </div>
              </div>
            )}
          </div>
        </div>
        </Modal.Content>
    </PositionedModal>

      {canShare && selectedFileResource && shareModalOpen && (
        <KnowledgeBaseContentShareModal
          fileId={selectedFileResource.id}
          fileName={selectedFileResource.name}
          isOpen={shareModalOpen}
          setModalOpen={setShareModalOpen}
        />
      )}

      <Modal
        open={isFeedbackModalOpen}
        onClose={handleCloseFeedbackModal}
        size="small"
        closeIcon
        dimmer={{ style: { zIndex: 11002 } }}
        style={{ zIndex: 11003, top: '50%', transform: 'translateY(-50%)' }}
      >
        <Modal.Header>We&apos;re sorry this content wasn&apos;t helpful.</Modal.Header>
        <Modal.Content>
          <div style={{ marginBottom: 16 }}>            
            We&apos;d be grateful for more detail so we can improve future recommendations.
          </div>
          <Form>
            <Form.TextArea
              placeholder="Optional feedback notes..."
              value={feedbackNotes}
              onChange={(_, data) => setFeedbackNotes(data.value as string)}
              style={{ minHeight: 150 }}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCloseFeedbackModal} disabled={isVoteLoading}>
            Cancel
          </Button>
          <Button primary loading={isVoteLoading} onClick={handleSubmitFeedback}>
            Submit
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

